import clsx from "clsx";
import React, { useRef } from "react";

import { TooltipPlacement } from "@/enums";
import { type CommonProps } from "@/types";
import { createId } from "@/utils/helpers";

import styles from "./Tooltip.module.css";

const ref = "tooltip";
const ARROW_HEIGHT = 12;

interface TooltipProps extends CommonProps {
  children: React.ReactNode;
  tooltip: React.ReactNode;
  placement?: TooltipPlacement;
  contentWidth?: number;
}

function useTooltip(props: TooltipProps) {
  const { color, placement, contentWidth } = props;
  const className = clsx(styles[ref], color && styles[color], props.className);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const containerTooltipRef = React.useRef<HTMLDivElement>(null);

  return {
    className,
    id: props.id || createId(ref),
    tooltip: props.tooltip || "",
    tooltipRef,
    placement: placement ?? TooltipPlacement.BOTTOM,
    contentWidth,
    containerTooltipRef,
  };
}

const calculateArrowBaseWidth = () =>
  Math.sqrt(Math.pow(ARROW_HEIGHT, 2) + Math.pow(ARROW_HEIGHT, 2));

function Tooltip(props: TooltipProps) {
  const {
    className,
    id,
    tooltip,
    tooltipRef,
    placement,
    contentWidth,
    containerTooltipRef,
  } = useTooltip(props);
  const arrowBaseWidth = Math.round(calculateArrowBaseWidth());
  const isTouch = window.matchMedia("(pointer: coarse)").matches;
  const tooltipContainer = useRef<HTMLElement>(null);
  const isOverflowed = useRef(false);

  const isOverflowedViewPort = () => {
    const { current } = tooltipRef;
    const { current: containerCurrent } = containerTooltipRef;

    if (!current || !containerCurrent) return false;

    const { bottom } = containerCurrent.getBoundingClientRect();
    const tooltipBottom = bottom + current.clientHeight;
    return tooltipBottom > window.innerHeight;
  };

  const calculateLeftAnchor = (left: number, contentWidth: number) => {
    let anchor = left - contentWidth / 2 + arrowBaseWidth / 2;

    if (placement === TooltipPlacement.BOTTOM_RIGHT) {
      anchor = left - contentWidth + arrowBaseWidth * 1.5;
    }

    return Math.floor(anchor);
  };

  const setTooltipContentPadding = () => {
    const { current } = tooltipRef;

    if (!current) return;

    const paddingValue = `${ARROW_HEIGHT * 1.5}px`;

    current.style.setProperty(
      "padding-top",
      isOverflowed.current ? "0px" : paddingValue,
    );
    current.style.setProperty(
      "padding-bottom",
      isOverflowed.current ? paddingValue : "0px",
    );
  };

  const setArrowTopPosition = (container: HTMLElement | null) => {
    let top = `calc(100% + ${ARROW_HEIGHT / 2}px)`;

    if (!container) return;

    if (isOverflowed.current) {
      top = `-${ARROW_HEIGHT + ARROW_HEIGHT / 2}px`;
    }

    container.style.setProperty("--arrow-top", top);
  };

  const setArrowRotate = (container: HTMLElement | null) => {
    if (!container) return;
    container.style.setProperty(
      "--arrow-rotate",
      isOverflowed.current ? "-135deg" : "45deg",
    );
  };

  const setContentPosition = (container: HTMLElement | null) => {
    const { current } = tooltipRef;

    if (!current || !container) return;

    const { top, bottom, left } = container.getBoundingClientRect();
    const x = calculateLeftAnchor(left, current.offsetWidth);
    const arrowHeight = ARROW_HEIGHT / 2;

    let y: number;

    if (isOverflowed.current) {
      const tooltipTop = top + window.scrollY;
      y = Math.floor(tooltipTop - current.clientHeight) + arrowHeight;
    } else {
      const tooltipTop = bottom + window.scrollY;
      y = Math.floor(tooltipTop) - arrowHeight;
    }

    container.style.setProperty("--calc-x", `${Math.max(x, 12)}px`);
    container.style.setProperty("--calc-y", `${y}px`);
  };

  const showContent = ({ target }: React.MouseEvent<HTMLElement>) => {
    const container = (target as HTMLElement).closest("article");
    const { current } = tooltipRef;

    if (!container || !current) {
      return;
    }

    tooltipContainer.current = container;
    current.showPopover();
    isOverflowed.current = isOverflowedViewPort();

    setTooltipContentPadding();
    setArrowRotate(container);
    setArrowTopPosition(container);

    // setContentPosition should be trigged at last
    setContentPosition(container);

    if (navigator.vendor.includes("Apple")) {
      container.style.setProperty("--inset", "0");
    }
  };

  const hideContent = () => {
    const { current } = tooltipRef;
    const container = tooltipContainer.current;

    if (!current) {
      return;
    }

    current.hidePopover();

    // Reset container styles to default
    if (container) {
      container.style.removeProperty("--calc-y");
    }
  };

  return (
    <article
      ref={containerTooltipRef}
      className={className}
      id={id}
      onMouseEnter={showContent}
      onClick={(e) => {
        if (tooltipRef.current?.contains(e.target as Node) && isTouch) {
          hideContent();
          return;
        }

        showContent(e);
      }}
      onMouseLeave={hideContent}
      style={
        {
          "--arrow-height": `${ARROW_HEIGHT}}px`,
          ...(contentWidth && { "--content-width": `${contentWidth}px` }),
        } as React.CSSProperties
      }
    >
      {props.children}

      <div
        ref={tooltipRef}
        className={styles["tooltip-content"]}
        popover={"auto"}
      >
        <span>{tooltip}</span>
      </div>
    </article>
  );
}

export default Tooltip;
export { Tooltip, type TooltipProps };
